import { formatDate, formatMoney, useLocale, useTranslation } from '@multaqa/i18n'
import { EmptyState } from '@multaqa/ui/components/ui/empty-state'
import { SkeletonTable } from '@multaqa/ui/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@multaqa/ui/components/ui/table'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useMemo } from 'react'
import { InlineQueryError } from '@/components/inline-query-error'
import { TableScroll } from '@/components/table-scroll'
import { meQuery } from '@/lib/me'
import { useCustomerInvoices, useCustomerPayments } from '../hooks/use-customer-accounting'

export function CustomerInvoicesPanel({ customerId }: { customerId: string }) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const { data: me } = useQuery(meQuery)
  const invoices = useCustomerInvoices(me?.active_company_id ?? null, customerId)

  if (!me?.active_company_id) return null
  const rows = invoices.data ?? []
  const panelState = getCustomerPanelState({
    panel: 'invoices',
    loading: invoices.isLoading,
    error: invoices.isError,
    empty: rows.length === 0,
    columns: [3, 2, 2, 2],
    skeletonRows: 4,
    onRetry: () => void invoices.refetch(),
    t,
  })
  if (panelState) return panelState

  return (
    <TableScroll>
      <Table data-testid="customer-invoices-table">
        <TableHeader>
          <TableRow className="bg-muted hover:bg-muted">
            <TableHead>{t('customer.hub.columns.document')}</TableHead>
            <TableHead>{t('customer.hub.columns.date')}</TableHead>
            <TableHead>{t('customer.hub.columns.dueDate')}</TableHead>
            <TableHead className="text-end">{t('customer.hub.columns.amountDue')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((invoice) => (
            <TableRow key={invoice.id} data-testid={`customer-invoice-row-${invoice.id}`}>
              <TableCell>
                <Link
                  to="/accounting/invoices/$invoiceId"
                  params={{ invoiceId: invoice.id }}
                  className="tabular-nums hover:underline"
                >
                  {invoice.document_number ? (
                    <bdi dir="ltr">{invoice.document_number}</bdi>
                  ) : (
                    t('customer.hub.draft')
                  )}
                </Link>
              </TableCell>
              <TableCell className="tabular-nums">
                {formatDate(new Date(invoice.invoice_date), locale)}
              </TableCell>
              <TableCell className="tabular-nums">
                {invoice.due_date ? formatDate(new Date(invoice.due_date), locale) : '—'}
              </TableCell>
              <TableCell
                className="text-end tabular-nums"
                data-testid={`customer-invoice-amount-due-${invoice.id}`}
              >
                {formatMoney(invoice.amount_due, invoice.currency, locale)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableScroll>
  )
}

export function CustomerPaymentsPanel({ customerId }: { customerId: string }) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const { data: me } = useQuery(meQuery)
  const payments = useCustomerPayments(me?.active_company_id ?? null, customerId)
  const received = useMemo(
    () =>
      (payments.data ?? []).filter(
        (payment) =>
          payment.direction === 'inbound' &&
          payment.partner_type === 'customer' &&
          payment.partner_id === customerId,
      ),
    [customerId, payments.data],
  )

  if (!me?.active_company_id) return null
  const panelState = getCustomerPanelState({
    panel: 'payments',
    loading: payments.isLoading,
    error: payments.isError,
    empty: received.length === 0,
    columns: [2, 2, 3, 2],
    skeletonRows: 4,
    onRetry: () => void payments.refetch(),
    t,
  })
  if (panelState) return panelState

  return (
    <TableScroll>
      <Table data-testid="customer-payments-table">
        <TableHeader>
          <TableRow className="bg-muted hover:bg-muted">
            <TableHead>{t('customer.hub.columns.document')}</TableHead>
            <TableHead>{t('customer.hub.columns.date')}</TableHead>
            <TableHead>{t('customer.hub.columns.reference')}</TableHead>
            <TableHead className="text-end">{t('customer.hub.columns.amount')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {received.map((payment) => (
            <TableRow key={payment.id} data-testid={`customer-payment-row-${payment.id}`}>
              <TableCell className="tabular-nums">
                <Link
                  to="/accounting/payments/$paymentId"
                  params={{ paymentId: payment.id }}
                  className="hover:underline"
                >
                  {payment.payment_number ? (
                    <bdi dir="ltr">{payment.payment_number}</bdi>
                  ) : (
                    t('customer.hub.draft')
                  )}
                </Link>
              </TableCell>
              <TableCell className="tabular-nums">
                {formatDate(new Date(payment.payment_date), locale)}
              </TableCell>
              <TableCell dir="ltr" className="text-start">
                {payment.reference ?? '—'}
              </TableCell>
              <TableCell className="text-end tabular-nums">
                {formatMoney(payment.amount, payment.currency, locale)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableScroll>
  )
}

export function getCustomerPanelState({
  panel,
  loading,
  error,
  empty,
  columns,
  skeletonRows,
  onRetry,
  t,
}: {
  panel: 'invoices' | 'payments' | 'statement'
  loading: boolean
  error: boolean
  empty: boolean
  columns: number[]
  skeletonRows: number
  onRetry: () => void
  t: (key: string) => string
}) {
  if (loading) return <SkeletonTable columns={columns} rows={skeletonRows} />
  if (error) {
    return (
      <InlineQueryError
        title={t('error.asyncTitle')}
        description={t(`customer.hub.${panel}LoadError`)}
        retryLabel={t('action.retry')}
        onRetry={onRetry}
      />
    )
  }
  if (empty) {
    return (
      <EmptyState
        title={t(`customer.hub.${panel}Empty`)}
        description={t(`customer.hub.${panel}EmptyDescription`)}
      />
    )
  }
  return null
}
